import { Injectable } from "@angular/core";
import { Observable, BehaviorSubject, of, combineLatest, forkJoin } from "rxjs";
import { map, catchError, shareReplay, startWith } from "rxjs/operators";
import { CalAngularService } from "@cvx/cal-angular";
import { GroupUtils } from "../../utils/group-names";
import { GroupConfigService } from "../group-config.service";

/**
 * Interface for user permissions
 */
export interface UserPermissions {
  isTurbineUser: boolean;
  isRamPlanner: boolean;
  isTechnicalLetterViewer: boolean;
  isTechnicalLetterAdmin: boolean;
  canAccessTurbines: boolean;
  canAccessRamTargets: boolean;
  canViewTechnicalLetters: boolean;
  canAdminTechnicalLetters: boolean;
}

/**
 * Service for managing user group membership and permissions
 */
@Injectable({
  providedIn: "root",
})
export class UserPermissionsService {
  private _permissions$ = new BehaviorSubject<UserPermissions | null>(null);
  private _isLoading$ = new BehaviorSubject<boolean>(false);

  /**
   * Observable of current user permissions
   */
  public readonly permissions$ = this._permissions$.asObservable();

  /**
   * Observable indicating if permissions are currently being loaded
   */
  public readonly isLoading$ = this._isLoading$.asObservable();
  constructor(
    private calAngularService: CalAngularService,
    private groupConfigService: GroupConfigService
  ) {
    this.loadUserPermissions();
  }
  /**
   * Loads user permissions by checking group membership
   */
  public loadUserPermissions(): void {
    this._isLoading$.next(true);
    const groupChecks = {
      turbineUser: this.checkGroupMembership(
        this.groupConfigService.getTurbineUserGroups()
      ),
      ramPlanner: this.checkGroupMembership(
        this.groupConfigService.getRamPlannerGroups()
      ),
      techLetterViewer: this.checkGroupMembership(
        this.groupConfigService.getTechnicalLetterViewerGroups()
      ),
      techLetterAdmin: this.checkGroupMembership(
        this.groupConfigService.getTechnicalLetterAdminGroups()
      ),
    };

    combineLatest([
      groupChecks.turbineUser,
      groupChecks.ramPlanner,
      groupChecks.techLetterViewer,
      groupChecks.techLetterAdmin,
    ])
      .pipe(
        map(
          ([
            isTurbineUser,
            isRamPlanner,
            isTechnicalLetterViewer,
            isTechnicalLetterAdmin,
          ]) => {
            const permissions: UserPermissions = {
              isTurbineUser,
              isRamPlanner,
              isTechnicalLetterViewer,
              isTechnicalLetterAdmin,
              canAccessTurbines: isTurbineUser,
              canAccessRamTargets: isRamPlanner,
              canViewTechnicalLetters:
                isTechnicalLetterViewer || isTechnicalLetterAdmin,
              canAdminTechnicalLetters: isTechnicalLetterAdmin,
            };
            return permissions;
          }
        ),
        catchError((error) => {
          console.error("Error loading user permissions:", error);
          return of(this.getDefaultPermissions());
        })
      )
      .subscribe((permissions) => {
        this._permissions$.next(permissions);
        this._isLoading$.next(false);
      });
  }

  /**
   * Refreshes user permissions (useful after login/logout)
   */
  public refreshPermissions(): void {
    this.loadUserPermissions();
  }
  /**
   * Checks if user is in any of the groups for a specific role
   * Handles comma-separated group names similar to API behavior
   */ private checkGroupMembership(groupName: string): Observable<boolean> {
    const individualGroups = GroupUtils.splitGroups(groupName);
    // If only one group, use the existing logic
    if (individualGroups.length === 1) {
      return this.calAngularService.isInGroup(individualGroups[0]).pipe(
        map((isMember) => {
          return isMember;
        }),
        catchError((error) => {
          console.error(
            `Error checking group membership for ${individualGroups[0]}:`,
            error
          );
          return of(false);
        }),
        shareReplay(1)
      );
    } // For multiple groups, check if user is in ANY of them
    const groupChecks = individualGroups.map((group) =>
      this.calAngularService.isInGroup(group).pipe(
        map((isMember) => {
          return isMember;
        }),
        catchError((error) => {
          console.error(`Error checking group membership for ${group}:`, error);
          return of(false);
        })
      )
    );

    return forkJoin(groupChecks).pipe(
      map((results: boolean[]) => results.some((result) => result === true)),
      catchError((error) => {
        console.error(
          `Error checking group membership for ${groupName}:`,
          error
        );
        return of(false);
      }),
      shareReplay(1)
    );
  }

  /**
   * Returns default permissions (all false) in case of errors
   */
  private getDefaultPermissions(): UserPermissions {
    return {
      isTurbineUser: false,
      isRamPlanner: false,
      isTechnicalLetterViewer: false,
      isTechnicalLetterAdmin: false,
      canAccessTurbines: false,
      canAccessRamTargets: false,
      canViewTechnicalLetters: false,
      canAdminTechnicalLetters: false,
    };
  }

  /**
   * Utility method to check if user has any permission
   */
  public hasAnyPermission(): Observable<boolean> {
    return this.permissions$.pipe(
      map((permissions) => {
        if (!permissions) return false;
        return (
          permissions.canAccessTurbines ||
          permissions.canAccessRamTargets ||
          permissions.canViewTechnicalLetters
        );
      })
    );
  }

  /**
   * Utility method to check specific permission
   */
  public hasPermission(
    permissionCheck: (permissions: UserPermissions) => boolean
  ): Observable<boolean> {
    return this.permissions$.pipe(
      map((permissions) => {
        if (!permissions) return false;
        return permissionCheck(permissions);
      })
    );
  }

  /**
   * Synchronous method to get current permissions (may return null if not loaded)
   */
  public getCurrentPermissions(): UserPermissions | null {
    return this._permissions$.value;
  }
}
